import DialogContent from '@/components/dialog-content.vue';
import StyleNumberImage from '@/components/style-number-image.vue';
import _ from 'lodash';

const formatAmount = (value) => {
  return value.toLocaleString('zh-CN', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

export const SalesLineItems = {
  functional: true,
  render: (h, { props }) => {
    const { value, showImage = false, showShippedInfo = false, wrapperStyle = {} } = props;
    const valueWithSalesAmount = value.map(item => ({
      ...item,
      sales_amount: (Number(item.price) * Number(item.quantity)) || 0
    }));

    const columns = [
      {
        prop: 'style_number',
        label: '款号',
        minWidth: 120,
      },
      {
        prop: 'quantity',
        label: '数量',
        minWidth: 120,
        renderCell: 'app-locale-string',
      },
      {
        prop: 'price',
        label: '价格',
        minWidth: 120,
        renderCell: 'app-locale-string',
      },
      {
        prop: 'cost_price',
        label: '成本价',
        minWidth: 120,
        renderCell: 'app-locale-string',
      },
      {
        prop: 'cost',
        label: '成本',
        minWidth: 120,
        renderCell: 'app-locale-string',
      },
      {
        prop: 'sales_amount',
        label: '销售金额',
        minWidth: 120,
        renderCell: 'app-locale-string',
      },
      showShippedInfo && {
        prop: 'shipped_info',
        label: '发货信息',
        minWidth: 700,
        renderCell: (h, { props: { scope: { row } } }) => {
          const shippedQtyTotal = _.sumBy(row.shipped_info || [], 'qty');
          const columns = [
            {
              prop: 'jushuitan_order_o_id',
              label: '内部单号'
            },
            {
              prop: 'payment_amount',
              label: '已收金额',
              renderCell: 'app-locale-string'
            },
            {
              prop: 'amount',
              label: '发货金额',
              renderCell: 'app-locale-string'
            },
            {
              prop: 'qty',
              label: '数量'
            },
            {
              prop: 'cost_price',
              label: '成本价',
              renderCell: 'app-locale-string'
            },
            {
              prop: 'cost',
              label: '成本',
              renderCell: 'app-locale-string'
            },
            {
              prop: 'adjustment_amount',
              label: '调整金额',
              renderCell: 'app-locale-string'
            },
            {
              prop: 'after_sale_amount',
              label: '售后金额',
              renderCell: 'app-locale-string'
            },
            {
              prop: 'after_sale_qty',
              label: '售后数量'
            }
          ];
          return <bean-table value={row.shipped_info} columns={columns} row-class-name={row.quantity === shippedQtyTotal ? '' : 'unshipped-order'} />;
        }
      },
      showImage && {
        prop: 'image',
        label: '图片',
        minWidth: 120,
        renderCell: (h, { props: { scope: { row } } }) => {
          return <StyleNumberImage styleNumber={row.style_number} />;
        },
      }
    ].filter(Boolean);
    const maxCount = 3;
    if (!valueWithSalesAmount || !valueWithSalesAmount.length) {
      return <span>/</span>;
    }
    const tableData = valueWithSalesAmount.slice(0, maxCount);

    const summaryMethod = ({ columns, data }) => {
      return columns.map((column, index) => {
        if (index === 0) {
          return '合计';
        }

        if (['quantity', 'price', 'cost', 'sales_amount'].includes(column.property)) {
          return formatAmount(_.sumBy(data, item => Number(item[column.property]) || 0));
        }
        return '';
      });
    }

    const tableRowClassName = ({ row }) => {
      // showShippedInfo 是在订单详情里面，不用加，否则就冲突了
      if (showShippedInfo) {
        return '';
      }
      return row.quantity === _.sumBy(row.shipped_info || [], 'qty') ? '' : 'unshipped-order'
    }

    return (
      <div style={{ width: `${120 * columns.length + 10}px`, textAlign: 'center', ...wrapperStyle }}>
        <bean-table
          size="mini"
          data={tableData}
          columns={columns}
          show-summary={value.length <= maxCount}
          summary-method={summaryMethod}
          row-class-name={tableRowClassName}
          height={null}
        />
        {
          value.length > maxCount && (
            <DialogContent title="订单项" triggerText={`共${value.length}条，查看全部`} triggerButtonProps={{ type: 'text', size: 'mini' }}>
              <bean-table
                height="800px"
                max-height="75vh"
                size="mini"
                data={valueWithSalesAmount}
                columns={columns}
                show-summary
                summary-method={summaryMethod}
              />
            </DialogContent>
          )
        }
      </div>
    )
  },
};

export const JushuitanOrders = {
  functional: true,
  render: (h, { props }) => {
    const { value, showFields = ['o_id'] } = props;
    const allColumns = [
      {
        prop: 'o_id',
        label: '订单号',
        minWidth: 120,
      },
      {
        prop: 'real_freight',
        label: '快递费',
        minWidth: 120,
        renderCell: 'app-locale-string'
      },
      {
        prop: 'cost',
        label: '销售成本',
        minWidth: 120,
        renderCell: 'app-locale-string'
      },
      {
        prop: 'profit',
        label: '毛利',
        minWidth: 120,
        renderCell: 'app-locale-string'
      }
    ];
    const columns = allColumns.filter(column => showFields.includes(column.prop));

    const maxCount = 3;
    if (!value || !value.length) {
      return <span>/</span>;
    }
    const tableData = value.slice(0, maxCount);
    return (
      <div style={{ width: `${120 * columns.length + 10}px`, textAlign: 'center' }}>
        <bean-table
          size="mini"
          data={tableData}
          columns={columns}
          height={null}
        />

        {
          value.length > maxCount && (
            <DialogContent title="聚水潭订单" triggerText={`共${value.length}条，查看全部`} triggerButtonProps={{ type: 'text', size: 'mini' }}>
              <bean-table size="mini" data={value} columns={columns} />
            </DialogContent>
          )
        }
      </div>
    )
  }
}

export const orderTableColumns = [
  {
    prop: 'number',
    label: '销售单号',
    minWidth: 180,
    fixed: 'left'
  },
  {
    prop: 'finished',
    label: '完结状态',
    minWidth: 120,
    renderCell: (h, { props: { value } }) => {
      return (
        <el-tag type={value ? 'success' : 'danger'}>{value ? '已完结' : '未完结'}</el-tag>
      );
    }
  },
  {
    prop: 'finished_on',
    label: '完结时间',
    minWidth: 120
  },
  {
    prop: 'ordered_on',
    label: '下单时间',
    minWidth: 120
  },
  {
    prop: 'last_sent_date',
    label: '发货时间',
    minWidth: 160
  },
  {
    prop: 'kind',
    label: '类型',
    minWidth: 120,
    renderCell(h, { props: { value } }) {
      if (value === 'sample') {
        return <el-tag type="primary">样板</el-tag>
      }
      if (value === 'bulk') {
        return <el-tag type="success">大货</el-tag>
      }
      if (value === 'after_sale') {
        return <el-tag type="warn">售后&退货</el-tag>
      }
      return <el-tag type="info">{value}</el-tag>
    }
  },
  {
    prop: 'product_type',
    label: '产品类型',
    minWidth: 120,
    renderCell(h, { props: { value } }) {
      if (value === 'custom') {
        return <el-tag type="primary">定制</el-tag>
      }
      if (value === 'stock') {
        return <el-tag type="success">现货</el-tag>
      }
      return <el-tag type="info">{value}</el-tag>
    }
  },
  {
    prop: 'business_kind',
    label: '业务类型',
    minWidth: 120,
    renderCell(h, { props: { value } }) {
      if (!value) {
        return <span>/</span>
      }
      if (value === 'custom') {
        return <el-tag type="primary">定制</el-tag>
      }
      if (value === 'wholesale') {
        return <el-tag type="success">批发</el-tag>
      }
      return <el-tag type="info">{value}</el-tag>
    }
  },
  {
    prop: 'customer',
    label: '客户',
    minWidth: 160,
    renderCell: (h, { props: { value } }) => {
      return (
        <bean-link keepNode target="_blank" to={{ name: 'customers.show', params: { id: value.id } }}>{value.name}</bean-link>
      );
    }
  },
  {
    prop: 'sales_amount',
    label: '下单金额',
    renderCell: 'app-locale-string',
    minWidth: 120
  },
  {
    prop: 'sales_line_items',
    label: '订单项',
    minWidth: 120 * 5 + 30,
    renderCell: (h, { props: { value } }) => {
      return <SalesLineItems value={value} />;
    }
  },
  {
    prop: 'received_amount',
    label: '已收金额',
    minWidth: 120,
    renderCell: 'app-locale-string',
  },
  {
    prop: 'receivable_amount',
    label: '应收金额',
    minWidth: 120,
    renderCell: 'app-locale-string',
  },
  {
    prop: 'jushuitan_orders',
    label: '聚水潭订单',
    minWidth: 120 * 1 + 30,
    renderCell: (h, { props: { value } }) => {
      return <JushuitanOrders value={value} showFields={['o_id']} />;
    }
  },
];
